import React, { useState } from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import { Plus, Users, Target, Award, TrendingUp, Crown, History } from 'lucide-react';
import {
  PageHeader, KpiCard, StatusChip, Panel, ProgressRow, FormDialog, LoadGate, PageShell, CHART_FONT,
} from './businessUi';
import { formatCurrency } from './erpData';
import { useErp } from './useErp';
import { ERP_KEYS } from '../../Services/app/erpservice';
import { StatusDialog, StatusHistoryDialog, withStatusChange } from './statusHistory';

const KEYS = { leads: ERP_KEYS.crm };

// Sales pipeline stages, in order. Won/Lost are terminal (not "open" pipeline).
const STAGES = ['Lead', 'Qualified', 'Proposal', 'Negotiation', 'Won', 'Lost'];
const OPEN_STAGES = ['Lead', 'Qualified', 'Proposal', 'Negotiation'];
const stageTone = { Lead: 'secondary', Qualified: 'sky', Proposal: 'primary', Negotiation: 'warning', Won: 'success', Lost: 'destructive' };
const STAGE_COLORS = { Lead: '#64748b', Qualified: '#0ea5e9', Proposal: '#7c5cff', Negotiation: '#f59e0b', Won: '#10b981', Lost: '#f43f5e' };
const pipeTone = { Lead: 'secondary', Qualified: 'sky', Proposal: 'primary', Negotiation: 'warning' };

const ADD_FIELDS = [
  { name: 'company', label: 'Company / Account', required: true },
  { name: 'contact', label: 'Contact Person', required: true },
  { name: 'owner', label: 'Account Owner', helper: 'Sales rep handling this account' },
  { name: 'source', label: 'Source', type: 'select', default: 'Website', options: ['Website', 'Referral', 'Cold Call', 'Exhibition', 'Existing Customer'] },
  { name: 'stage', label: 'Stage', type: 'select', default: 'Lead', options: STAGES },
  { name: 'value', label: 'Deal Value (INR)', type: 'number', default: 0 },
  { name: 'date', label: 'Created', type: 'date', default: '2026-06-13' },
];

export default function Crm() {
  const { data, loading, error, save, reload } = useErp(KEYS);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusIndex, setStatusIndex] = useState(null);
  const [historyId, setHistoryId] = useState(null);

  const leads = data?.leads || [];
  const handleAdd = (v) => {
    const next = [{ id: `LEAD-${1000 + leads.length + 1}`, ...v }, ...leads];
    save('leads', next).catch(() => window.alert('Failed to save to ThingsBoard. Please retry.'));
  };

  // Record a stage change with its date; status follows and history is appended.
  const handleStatusUpdate = (v) => {
    if (statusIndex === null) return;
    const next = leads.map((l, i) => (i === statusIndex ? withStatusChange(l, v, 'stage') : l));
    save('leads', next).catch(() => window.alert('Failed to save to ThingsBoard. Please retry.'));
  };

  const historyLead = leads.find((l) => l.id === historyId) || null;

  const openLeads = leads.filter((l) => OPEN_STAGES.includes(l.stage));
  const pipelineValue = openLeads.reduce((s, l) => s + (Number(l.value) || 0), 0);
  const wonValue = leads.filter((l) => l.stage === 'Won').reduce((s, l) => s + (Number(l.value) || 0), 0);
  const wonCount = leads.filter((l) => l.stage === 'Won').length;
  const lostCount = leads.filter((l) => l.stage === 'Lost').length;
  const winRate = wonCount + lostCount > 0 ? Math.round((wonCount / (wonCount + lostCount)) * 100) : 0;

  // Pipeline value per open stage (horizontal bar).
  const stageValue = OPEN_STAGES.map((s) => leads.filter((l) => l.stage === s).reduce((a, l) => a + (Number(l.value) || 0), 0));
  const pipelineOptions = {
    chart: { type: 'bar', toolbar: { show: false }, fontFamily: CHART_FONT },
    plotOptions: { bar: { horizontal: true, borderRadius: 6, borderRadiusApplication: 'end', barHeight: '55%', distributed: true } },
    colors: OPEN_STAGES.map((s) => STAGE_COLORS[s]),
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: { categories: OPEN_STAGES, labels: { formatter: (v) => `${Math.round(v / 1000)}k`, style: { fontSize: '11px' } } },
    yaxis: { labels: { style: { fontSize: '12px' } } },
    tooltip: { y: { formatter: (v) => formatCurrency(v) } },
    grid: { borderColor: 'background.default' },
  };

  // Stage mix by count (donut).
  const stageCounts = STAGES.map((s) => leads.filter((l) => l.stage === s).length);
  const stageOptions = {
    chart: { type: 'donut', fontFamily: CHART_FONT, dropShadow: { enabled: true, top: 4, blur: 8, opacity: 0.12 } },
    labels: STAGES,
    colors: STAGES.map((s) => STAGE_COLORS[s]),
    legend: { position: 'bottom', fontSize: '12px' },
    dataLabels: { enabled: false },
    stroke: { width: 2 },
    plotOptions: { pie: { donut: { size: '70%', labels: { show: true, total: { show: true, label: 'Leads', fontSize: '12px', color: 'text.secondary', formatter: () => String(leads.length) } } } } },
  };

  // Open deals worth chasing, largest first.
  const hotDeals = [...openLeads].sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0)).slice(0, 5);
  const maxDeal = Math.max(1, ...hotDeals.map((d) => Number(d.value) || 0));

  return (
    <PageShell>
      <PageHeader
        title="CRM"
        subtitle="Customer relationships, leads & sales pipeline"
        actions={<Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={() => setDialogOpen(true)}>New Lead</Button>}
      />

      <FormDialog open={dialogOpen} title="New Lead / Account" fields={ADD_FIELDS} onClose={() => setDialogOpen(false)} onSubmit={handleAdd} submitLabel="Add Lead" />

      <StatusDialog
        open={statusIndex !== null}
        record={statusIndex !== null ? leads[statusIndex] : null}
        label="Stage"
        statusField="stage"
        options={STAGES}
        title={statusIndex !== null ? `Update Stage · ${leads[statusIndex].company}` : 'Update Stage'}
        onClose={() => setStatusIndex(null)}
        onSubmit={handleStatusUpdate}
      />

      <StatusHistoryDialog
        open={historyId !== null}
        record={historyLead}
        statusField="stage"
        toneFor={(s) => stageTone[s]}
        title={historyLead ? `${historyLead.company} · ${historyLead.id}` : 'Stage History'}
        subtitle="Lead stage history"
        onClose={() => setHistoryId(null)}
      />

      <LoadGate loading={loading} error={error} onRetry={reload}>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, mb: 2.5 }}>
          <KpiCard label="Total Leads" value={String(leads.length)} icon={Users} tone="primary" hint={`${openLeads.length} open`} />
          <KpiCard label="Open Pipeline" value={formatCurrency(pipelineValue)} icon={Target} tone="sky" />
          <KpiCard label="Won Value" value={formatCurrency(wonValue)} icon={Award} tone="success" hint={`${wonCount} deal(s)`} />
          <KpiCard label="Win Rate" value={`${winRate}%`} icon={TrendingUp} tone="warning" hint={`${wonCount} won · ${lostCount} lost`} />
        </Box>

        {leads.length === 0 ? (
          <Panel title="Sales Pipeline" accent="linear-gradient(90deg,#7c5cff,#818cf8)">
            <Typography sx={{ p: 4, textAlign: 'center', color: 'text.disabled', fontSize: 14 }}>
              No leads yet. Click <strong>New Lead</strong> to add your first account — it’s saved to ThingsBoard.
            </Typography>
          </Panel>
        ) : (
          <>
            <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' }, mb: 2.5 }}>
              <Panel title="Pipeline Value by Stage" subtitle="Open opportunities only" accent="linear-gradient(90deg,#7c5cff,#818cf8)">
                <Box sx={{ px: 2, pb: 1 }}>
                  <ReactApexChart options={pipelineOptions} series={[{ name: 'Value', data: stageValue }]} type="bar" height={260} />
                </Box>
              </Panel>

              <Panel title="Stage Mix" subtitle="All leads by stage" accent="linear-gradient(90deg,#0ea5e9,#38bdf8)">
                <Box sx={{ px: 2, pb: 2 }}>
                  <ReactApexChart options={stageOptions} series={stageCounts} type="donut" height={260} />
                </Box>
              </Panel>
            </Box>

            <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, mb: 2.5 }}>
              <Panel title="Stage Funnel" subtitle="Lead count moving through the pipeline" accent="linear-gradient(90deg,#f59e0b,#fbbf24)">
                <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
                  {OPEN_STAGES.map((s) => {
                    const count = leads.filter((l) => l.stage === s).length;
                    const max = Math.max(1, ...OPEN_STAGES.map((x) => leads.filter((l) => l.stage === x).length));
                    return <ProgressRow key={s} label={s} value={count} max={max} tone={pipeTone[s]} valueLabel={`${count} lead${count === 1 ? '' : 's'}`} />;
                  })}
                </Box>
              </Panel>

              <Panel title="Hot Deals to Chase" subtitle="Largest open opportunities" accent="linear-gradient(90deg,#10b981,#34d399)" actions={<Crown size={18} color="#f59e0b" />}>
                <Box sx={{ px: 2, pt: 0.5, pb: 1 }}>
                  {hotDeals.length === 0 && <Typography sx={{ fontSize: 13, color: 'text.disabled', py: 2 }}>No open deals right now.</Typography>}
                  {hotDeals.map((d, i) => (
                    <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: i < hotDeals.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                      <Box sx={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 12, fontWeight: 700, color: i === 0 ? '#b45309' : '#475569', bgcolor: i === 0 ? 'rgba(245,158,11,0.16)' : 'rgba(100,116,139,0.12)' }}>
                        {i === 0 ? <Crown size={14} /> : i + 1}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.company}</Typography>
                        <Box sx={{ height: 5, borderRadius: 3, bgcolor: 'background.default', mt: 0.5, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${((Number(d.value) || 0) / maxDeal) * 100}%`, background: STAGE_COLORS[d.stage] || '#7c5cff', borderRadius: 3 }} />
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(Number(d.value) || 0)}</Typography>
                        <StatusChip label={d.stage} tone={stageTone[d.stage]} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Panel>
            </Box>

            <Panel title="All Leads & Accounts" subtitle="Click a row to view its stage history · use Update to move it along the pipeline" accent="linear-gradient(90deg,#7c5cff,#818cf8)">
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Company</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Owner</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell align="right">Value</TableCell>
                      <TableCell>Stage</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leads.map((l, idx) => {
                      const terminal = l.stage === 'Won' || l.stage === 'Lost';
                      return (
                        <TableRow key={l.id} hover sx={{ cursor: 'pointer' }} onClick={() => setHistoryId(l.id)}>
                          <TableCell sx={{ fontWeight: 600 }}>{l.company}</TableCell>
                          <TableCell>{l.contact}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{l.owner || '—'}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{l.source || '—'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(Number(l.value) || 0)}</TableCell>
                          <TableCell><StatusChip label={l.stage} tone={stageTone[l.stage]} /></TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            <Tooltip title={terminal ? 'Stage is closed — update to re-open' : 'Update stage with a date'}>
                              <Button size="small" variant="outlined" startIcon={<History size={15} />} sx={{ textTransform: 'none' }}
                                onClick={() => setStatusIndex(idx)}>
                                Update
                              </Button>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Panel>
          </>
        )}
      </LoadGate>
    </PageShell>
  );
}
